import { DateTime } from 'luxon';
import { WorkoutWithCycleAndSession } from '../../types';
import {
  getActualBowlCounts,
  getPrescriptionBowlCounts,
} from '../slots/workout-metric-utils';
import { ActualWorkout } from '../types';
import {
  ACUTE_WINDOW_DAYS,
  buildContiguousDays,
  CHRONIC_WINDOW_DAYS,
} from './chart-utils';

export type BowlingDay = {
  day: string;
  planned: number;
  actual: number | null;
  /** ACWR = 7-day rolling avg ÷ 28-day rolling weekly avg. Null until ≥28 days of history. */
  acwr: number | null;
};

/**
 * Builds the full per-day series shared by Charts 2 (Side-by-Side) and 3
 * (Stacked) from the unfiltered prescription and actual workout arrays.
 *
 * Operates on the full dataset (no date-range filtering) so that ACWR rolling
 * windows always have access to ≥28 days of history. Charts narrow the
 * visible range later via `clampDaysToDateRange` — mirrors Chart 1's behaviour.
 */
export function computeBowlingDays(
  prescriptionWorkouts: WorkoutWithCycleAndSession[],
  actualWorkouts: ActualWorkout[],
): BowlingDay[] {
  const byDay = new Map<string, BowlingDay>();

  for (const wo of prescriptionWorkouts) {
    const day = wo.assignedDate;
    if (!day) continue;
    const { planned } = getPrescriptionBowlCounts(wo);
    const existing = byDay.get(day) ?? makeEmpty(day);
    byDay.set(day, { ...existing, planned: existing.planned + planned });
  }

  for (const wo of actualWorkouts) {
    const day = wo.assignedDate;
    if (!day) continue;
    const { actual: actualBowls } = getActualBowlCounts(wo);
    if (actualBowls === undefined) continue;
    const existing = byDay.get(day) ?? makeEmpty(day);
    const runningActual = (existing.actual ?? 0) + actualBowls;
    byDay.set(day, { ...existing, actual: runningActual });
  }

  const contiguous = buildContiguousDays(byDay, makeEmpty);
  return withAcwr(contiguous);
}

function makeEmpty(day: string): BowlingDay {
  return { day, planned: 0, actual: null, acwr: null };
}

/**
 * Per-day "best available" load used as the ACWR signal. Days before today
 * are actual-only — a past day with no recorded actual contributes 0 rather
 * than falling back to its planned/prescribed value — prevents
 * planned-but-never-actualed history from inflating the rolling averages.
 * Today and future days keep the original fallback ("actual bowls when
 * hasActual is true, planned bowls otherwise") since there's no actual yet
 * to compare against.
 */
function ratioSourceFor(d: BowlingDay, todayIso: string): number {
  if (d.day < todayIso) return d.actual ?? 0;
  return d.actual ?? d.planned;
}

function withAcwr(days: BowlingDay[]): BowlingDay[] {
  const todayIso = DateTime.now().toISODate() ?? '';
  const readRatioSource = (d: BowlingDay) => ratioSourceFor(d, todayIso);
  return days.map((d, i) => {
    if (i < CHRONIC_WINDOW_DAYS - 1) return d;
    const acuteSum = sumWindow(days, i, ACUTE_WINDOW_DAYS, readRatioSource);
    const chronicWeeklyAvg =
      sumWindow(days, i, CHRONIC_WINDOW_DAYS, readRatioSource) /
      (CHRONIC_WINDOW_DAYS / ACUTE_WINDOW_DAYS);
    if (chronicWeeklyAvg === 0) return d;
    return { ...d, acwr: acuteSum / chronicWeeklyAvg };
  });
}

function sumWindow(
  days: BowlingDay[],
  endIndex: number,
  windowSize: number,
  read: (d: BowlingDay) => number,
): number {
  let sum = 0;
  for (let i = endIndex - windowSize + 1; i <= endIndex; i++) {
    const day = days[i];
    if (day) sum += read(day);
  }
  return sum;
}

/**
 * Bounds the visible day range to the date picker's window. Presentation-only
 * — ACWR values remain those computed against the full unbounded history.
 */
export function clampDaysToDateRange(
  days: BowlingDay[],
  dateRange: { start: string; end: string },
): BowlingDay[] {
  return days.filter((d) => d.day >= dateRange.start && d.day <= dateRange.end);
}
